import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import {
  clearInsertEmailError,
  clearInsertEmailFields,
  resetInsertEmailRequestState,
  setEmail
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { insertEmail } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { messagesForInsertEmailError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';

import appRoutes from '../../../../routing/app-routes';
import { InsertEmailError } from '../../../../store/features/user-access/user-registration/user-registration-types';

export interface InsertEmailPageLogicCallbacks {
  buttonDisabled: boolean;
  email: string;
  insertEmailRequestStatus: ApiRequestStatus;
  insertEmailError: InsertEmailError;

  updateEmail: (value?: string) => void;
  handleSubmit: () => void;
  handleClose: () => void;
}

export const useInsertEmailPageLogic = (
  actions: GlobalActions
): InsertEmailPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const { email, insertEmailRequestStatus, insertEmailError } = useAppSelector(
    (state) => state.features.userRegistration
  );

  const [buttonDisabled, setButtonDisabled] = useState(
    !email || email?.length === 0
  );

  const updateEmail = useCallback(
    (value: string) => {
      setButtonDisabled(false);
      dispatch(setEmail(value));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    dispatch(insertEmail());
  }, [dispatch]);

  useEffect(() => {
    if (insertEmailRequestStatus === 'succeeded') {
      dispatch(resetInsertEmailRequestState());
      navigate(appRoutes.EmailVerificationUserRegistration, { replace: false });
    }
  }, [dispatch, navigate, insertEmailRequestStatus]);

  useEffect(() => {
    if (insertEmailError) {
      if (
        insertEmailError === 'GENERIC_ERROR' ||
        insertEmailError === 'INVALID_EMAIL_FORMAT'
      ) {
        const errorMessages = messagesForInsertEmailError(insertEmailError);
        showToast({
          type: 'error',
          title: formatMessage(errorMessages.title),
          description:
            errorMessages.description &&
            formatMessage(errorMessages.description),
          close: closeToast,
        });
        dispatch(clearInsertEmailError());
      }
    }
  }, [dispatch, formatMessage, insertEmailError]);

  const handleClose = useCallback(() => {
    dispatch(resetInsertEmailRequestState());
    dispatch(clearInsertEmailFields());
  }, [dispatch, navigate]);

  return {
    buttonDisabled,
    email,
    insertEmailRequestStatus,
    insertEmailError,
    handleSubmit,
    updateEmail,
    handleClose,
  };
};
